<template>
  <div class="flex flex-col gap-2.5">
    <h2 class="text-sm font-semibold">Create Departments</h2>
    <div class="flex flex-col bg-primary/10 p-1.5 rounded">
      <div class="flex items-start gap-1.5">
        <i class="pi pi-info-circle text-primary mt-1 text-xs"></i>
        <span class="text-xs">
          Select a faculty, add departments, and click 'Save Faculty & Departments'.
          Repeat for as many faculties as you want, then click 'Submit All'.
        </span>
      </div>
    </div>

    <!-- Faculty and department input for current group -->
    <div class="flex flex-col gap-1.5">
      <label class="font-medium text-xs">Select Faculty</label>
      <Select v-model="currentFacultyId" :options="allFaculties" optionLabel="faculty_name" optionValue="id"
        placeholder="Select a faculty" class="w-full" />
    </div>
    <div class="flex flex-col gap-1.5">
      <label class="font-medium text-xs">Type</label>
      <Select v-model="currentType" :options="typeOptions" optionLabel="label" optionValue="value" class="w-full" />
    </div>
    <form @submit.prevent="addDepartment" class="flex gap-1.5 items-end">
      <div class="flex-1 flex flex-col gap-1.5">
        <label class="font-medium text-xs">Department Name</label>
        <InputText v-model="departmentName" placeholder="Enter department name" class="w-full" />
      </div>
      <Button type="submit" label="Add Dept" size="small" class="bg-primary"
        :disabled="!departmentName.trim() || !currentFacultyId" />
    </form>
    <ul v-if="currentDepartments.length" class="flex flex-col gap-1.5 mb-1.5">
      <li v-for="(dept, idx) in currentDepartments" :key="idx"
        class="flex items-center justify-between bg-gray-100 px-2.5 py-1.5 rounded">
        <span class="text-sm">{{ dept }}</span>
        <Button icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm"
          @click="removeCurrentDepartment(idx)" />
      </li>
    </ul>
    <div class="flex justify-end mb-1.5">
      <Button label="Save Faculty & Departments" size="small" @click="saveFacultyDepartments"
        :disabled="!currentFacultyId || currentDepartments.length === 0" class="!bg-primary !border-primary" />
    </div>

    <!-- Summary of all faculty/department groups -->
    <div v-if="facultyDepartments.length" class="border-t pt-3 mt-1.5">
      <h3 class="font-semibold mb-1.5 text-sm">To be submitted:</h3>
      <div v-for="group in facultyDepartments" :key="group.facultyId"
        class="mb-3 p-2.5 bg-gray-50 rounded border border-gray-200">
        <div class="flex items-center gap-2 mb-1.5">
          <strong class="text-gray-700 text-sm">{{ group.facultyName }}</strong>
          <span class="text-xs px-2 py-0.5 rounded-full" :class="group.type === 'academic' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'">
            {{ group.type === 'academic' ? 'Academic' : 'Non-Teaching' }}
          </span>
        </div>
        <ul class="ml-4 list-disc pl-4">
          <li v-for="dept in group.departments" :key="dept" class="text-xs text-gray-600">Department of {{ dept }}</li>
        </ul>
      </div>
    </div>

    <div class="flex justify-end mt-1.5">
      <Button @click="submitAll" :label="isSubmitting ? 'Submitting...' : 'Submit All'" size="small" :loading="isSubmitting"
        :disabled="facultyDepartments.length === 0" class="!bg-primary !border-primary" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useFaculties } from '@/composables/queries/useFaculties';

const emit = defineEmits(['success']);
const { createFacultyDepartment } = useTertiaryMutations();
const { faculties: allFaculties } = useFaculties();

const typeOptions = [
  { label: 'Academic', value: 'academic' },
  { label: 'Non-Teaching', value: 'non_teaching' },
];

const facultyDepartments = ref<{ facultyId: string, facultyName: string, type: string, departments: string[] }[]>([]);
const currentFacultyId = ref('');
const currentType = ref('academic');
const departmentName = ref('');
const currentDepartments = ref<string[]>([]);
const isSubmitting = ref(false);

function addDepartment() {
  if (departmentName.value.trim()) {
    currentDepartments.value.push(departmentName.value.trim());
    departmentName.value = '';
  }
}

function removeCurrentDepartment(idx: number) {
  currentDepartments.value.splice(idx, 1);
}

function saveFacultyDepartments() {
  if (!currentFacultyId.value || currentDepartments.value.length === 0) return;
  const facultyName = allFaculties.value.find(f => f.id === currentFacultyId.value)?.faculty_name || '';
  facultyDepartments.value.push({
    facultyId: currentFacultyId.value,
    facultyName,
    type: currentType.value,
    departments: [...currentDepartments.value],
  });
  currentFacultyId.value = '';
  currentType.value = 'academic';
  currentDepartments.value = [];
}

async function submitAll() {
  isSubmitting.value = true;
  for (const group of facultyDepartments.value) {
    for (const dept of group.departments) {
      try {
        await createFacultyDepartment({ department_name: dept, faculty_id: group.facultyId, type: group.type });
      } catch {
        // Skip the failed department and continue with the rest.
      }
    }
  }
  isSubmitting.value = false;
  emit('success');
}
</script>
